import './style.css';
import './components/NavigationStyles.css';
import './components/ModalStyles.css';
import './components/AdminStyles.css';
import './components/PackagePanelStyles.css';
import './components/WorkspaceStyles.css';
import { NavigationHeader } from './components/NavigationHeader';
import { ServiceRouter, DEFAULT_SERVICE, CONTEXT_EVENT, serviceFromHash, resolvePublicService } from './components/ServiceRouter';
import { Workspace } from './components/Workspace';
import { IntroScreen } from './components/IntroScreen';
import { currentProjectId, projectStore, setCurrentProjectId, PROJECT_EVENT } from './lib/ProjectContainer';

// Dev builds expose the container store so a headless check can inject a ledger and cue.
if (import.meta.env.DEV) (window as unknown as { __mokulabDev?: unknown }).__mokulabDev = { projectStore, setCurrentProjectId };

let navigationHeader: NavigationHeader;
let serviceRouter: ServiceRouter;
let workspace: Workspace;
let intro: IntroScreen;

document.addEventListener('DOMContentLoaded', async () => {
    await initializeWorkspace();
});

/**
 * Assemble the docked-panel shell: command bar (NavigationHeader), Navigator
 * dock, center view (ServiceRouter renders here), Info dock, status bar.
 */
async function initializeWorkspace(): Promise<void> {
    const app = document.getElementById('app');
    if (!app) throw new Error('App container not found');

    workspace = new Workspace(app);
    navigationHeader = new NavigationHeader();
    serviceRouter = new ServiceRouter();
    navigationHeader.setServiceRouter(serviceRouter);
    navigationHeader.renderInto(workspace.topbar);
    workspace.buildNavigator();
    workspace.setNavigateHandler(service => navigationHeader.switchToService(service));
    intro = new IntroScreen(workspace.main);
    intro.setNavigateHandler(service => navigationHeader.switchToService(service));

    setupGlobalEventListeners();

    const urlParams = new URLSearchParams(window.location.search);
    const adminMode = urlParams.get('admin') === 'true';
    const enableAdmin = import.meta.env.VITE_ENABLE_ADMIN === 'true';
    if (enableAdmin) await initializeAdminButton();

    if (adminMode && enableAdmin) {
        await navigationHeader.enterAdminMode();
    } else {
        // No hash and no container held yet → Start; otherwise the hash, else the lab.
        const bareHash = /^#?\/?$/.test(window.location.hash);
        const initial = bareHash && !currentProjectId() ? 'start' : (serviceFromHash(window.location.hash) ?? DEFAULT_SERVICE);
        navigationHeader.switchToService(initial);
        // The introduction: the network message, once per viewer, or on request.
        if (urlParams.get('welcome') === '1' || IntroScreen.shouldShow()) intro.show();
    }
    // Open the held container only after the first view is on screen: a load that settles
    // synchronously (signed out) would otherwise re-route to the lab before the deep link renders.
    if (currentProjectId()) void projectStore.load();
}

async function initializeAdminButton(): Promise<void> {
    try {
        const { SupabaseAuthProvider } = await import('./services/auth/SupabaseAuthProvider.js');
        const authProvider = SupabaseAuthProvider.getInstance();
        authProvider.onAuthStateChange((state) => navigationHeader?.updateAdminButton(state.isAuthenticated));
    } catch (error) {
        console.warn('Admin button initialization skipped:', error);
    }
}

function setupGlobalEventListeners(): void {
    // Navigation requests from the view, the Navigator, and the admin panel
    window.addEventListener('navigate', (event: Event) => {
        const requested = (event as CustomEvent).detail?.service as string | undefined;
        if (requested) navigationHeader.switchToService(resolvePublicService(requested) ?? DEFAULT_SERVICE);
    });

    // The router tells the workspace what is on screen
    window.addEventListener(CONTEXT_EVENT, (event: Event) => {
        workspace.setContext((event as CustomEvent).detail);
    });

    // The current container changed or (re)loaded: the Navigator starts at "now"
    window.addEventListener(PROJECT_EVENT, (event: Event) => {
        const detail = (event as CustomEvent).detail || {};
        if (!detail.loaded) { void projectStore.load(true); return; }
        workspace.buildNavigator();
        // The lab and the domain pages read the container's setup: re-render them once it has loaded.
        // Start and Setup hold forms in progress and are left alone.
        const current = navigationHeader.getCurrentService();
        if (!navigationHeader.getIsInAdminMode() && current !== 'start' && current !== 'setup') serviceRouter.switchService(current);
    });

    // "Details" buttons open the Info dock on a section
    window.addEventListener('ws-info', (event: Event) => {
        workspace.showInfo((event as CustomEvent).detail?.section);
    });
    window.addEventListener('ws-intro', () => intro.show());
    // Admin tools opened from a view (the viewer, from Models)
    window.addEventListener('ws-admin', (event: Event) => {
        if (import.meta.env.VITE_ENABLE_ADMIN !== 'true') return;
        void navigationHeader.enterAdminMode((event as CustomEvent).detail?.tab as string | undefined);
    });

    // Back/forward and hand-edited hashes
    window.addEventListener('hashchange', () => {
        if (navigationHeader.getIsInAdminMode()) return;
        const target = serviceFromHash(window.location.hash);
        if (target && target !== navigationHeader.getCurrentService()) navigationHeader.switchToService(target);
    });

    // Command bar dock toggles
    document.addEventListener('click', (event) => {
        const target = event.target as HTMLElement;
        if (target.closest('#info-modal-btn')) workspace.toggleRight();
        if (target.closest('#ws-toggle-left')) workspace.toggleLeft();
        if (target.closest('#ws-welcome-btn')) { if (intro.isOpen()) intro.hide(); else intro.show(); }
    });

    // Keyboard: F1 / Ctrl+I Info dock, Ctrl+B Navigator, Ctrl+1 Systems Lab
    document.addEventListener('keydown', (event) => {
        if (event.key === 'F1' || (event.ctrlKey && event.key.toLowerCase() === 'i')) {
            event.preventDefault();
            workspace.toggleRight();
        }
        if (event.ctrlKey && event.key.toLowerCase() === 'b') {
            event.preventDefault();
            workspace.toggleLeft();
        }
        if (event.ctrlKey && event.key === '1') {
            event.preventDefault();
            navigationHeader.switchToService(DEFAULT_SERVICE);
        }
    });
}
